import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Pricing() {
  const { t } = useTranslation();

  const plans = [
    {
      name: t('servicesPage.plan1Name'),
      description: t('servicesPage.plan1Desc'),
      price: t('servicesPage.plan1Price'),
      period: '',
      features: [
        { text: 'Fast-Track Aéroport', included: true },
        { text: 'Gestion des sinistres basique', included: true },
        { text: 'Reporting mensuel', included: true },
        { text: 'Support email', included: true },
        { text: 'Plateforme SaaS', included: false },
        { text: 'API d\'intégration', included: false },
        { text: 'Account Manager dédié', included: false }
      ],
      popular: false,
      gradient: 'from-gray-600 to-gray-700'
    },
    {
      name: t('servicesPage.plan2Name'),
      description: t('servicesPage.plan2Desc'),
      price: t('servicesPage.plan2Price'),
      period: '',
      features: [
        { text: 'Fast-Track Aéroport', included: true },
        { text: 'Gestion des sinistres complète', included: true },
        { text: 'Logistique & Livraison', included: true },
        { text: 'Plateforme SaaS complète', included: true },
        { text: 'Reporting temps réel', included: true },
        { text: 'Support prioritaire 24/7', included: true },
        { text: 'API d\'intégration', included: false }
      ],
      popular: true,
      gradient: 'from-[#FF6B6B] to-[#FF8E53]'
    },
    {
      name: t('servicesPage.plan3Name'),
      description: t('servicesPage.plan3Desc'),
      price: t('servicesPage.plan3Price'),
      period: '',
      features: [
        { text: 'Tous les services inclus', included: true },
        { text: 'Volume illimité', included: true },
        { text: 'API d\'intégration complète', included: true },
        { text: 'Account Manager dédié', included: true },
        { text: 'SLA personnalisé', included: true },
        { text: 'Formation équipes', included: true },
        { text: 'Audit & Conseil inclus', included: true }
      ],
      popular: false,
      gradient: 'from-[#4ECDC4] to-[#44A08D]'
    }
  ];

  return (
    <section className="py-20 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B6B]/10 rounded-full text-[#FF6B6B] text-sm font-semibold mb-4">
            <i className="ri-price-tag-3-line"></i>
            {t('servicesPage.pricingBadge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('servicesPage.pricingTitle')}
            <span className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] bg-clip-text text-transparent"> {t('servicesPage.pricingTitleHighlight')} </span>
            {t('servicesPage.pricingTitleEnd')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('servicesPage.pricingSubtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-2 ${
                plan.popular ? 'border-[#FF6B6B] scale-105' : 'border-gray-100'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-2 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white text-sm font-semibold rounded-full shadow-lg whitespace-nowrap">
                    {t('servicesPage.recommended')}
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${plan.gradient} rounded-2xl mb-4`}>
                  <i className="ri-building-2-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {plan.name}
                </h3>
                <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-3xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {plan.price}
                  </span>
                  {plan.period && <span className="text-gray-500">{plan.period}</span>}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      feature.included 
                        ? `bg-gradient-to-br ${plan.gradient}` 
                        : 'bg-gray-200'
                    }`}>
                      <i className={`${feature.included ? 'ri-check-line' : 'ri-close-line'} text-white text-sm`}></i>
                    </div>
                    <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="/contact"
                className={`block w-full py-4 rounded-xl font-semibold text-center transition-all cursor-pointer whitespace-nowrap ${
                  plan.popular
                    ? 'bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white hover:shadow-lg hover:shadow-[#FF6B6B]/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('servicesPage.planCta')}
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            <i className="ri-information-line mr-2"></i>
            {t('servicesPage.pricingNote')}
          </p>
        </div>
      </div>
    </section>
  );
}
